/**
 * Die Seite eines Behälters: Packstück oder Lagerort (KONZEPT.md §8.1, ADR 0013 §2).
 *
 * Was ein Lagerort- oder Packstück-QR öffnet. Oben, wo der Behälter steckt,
 * darunter die Aktionen, dann der Inhalt -- gleiche Artikel zusammengefasst,
 * Packstücke als eigene Zeilen.
 *
 * **Einbuchen** und **Ausbuchen** als Auswahl (ADR 0020, löst den Dauerscan
 * aus ADR 0013 §3 ab): ankreuzen oder scannen, gebucht wird erst mit „Fertig“,
 * als ein Vorgang, danach eine Bestätigung mit „Rückgängig“ für alles.
 * Ausbuchen heißt Verschieben zum Ziel oben -- nie mehr `entnehmen`.
 *
 * Ein Packstück mit Artikel lässt sich außerdem selbst **verschieben** und
 * bekommt einen **Status**. Ein fester Ort nicht -- er ist kein Artikel.
 *
 * **Stammplatz und Vollständigkeit** (ADR 0013 §6, §9, ADR 0015): Oben steht,
 * wo das Packstück hingehört, und wie vollständig der Behälter ist -- direkt
 * gezählt, dazu was in den Kisten darin fehlt. „Alles da“ bucht nach einer
 * Übersicht alles an seinen Stammplatz zurück. Beim Einbuchen lässt sich der
 * Behälter zugleich als Stammplatz festlegen; das braucht Netz.
 *
 * **Unbekannt** (ADR 0020): Dorthin bucht nur ein Status wie „verloren“,
 * deshalb gibt es dort kein Einbuchen.
 */

import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import type { Vollstaendigkeit, Ziel } from "@thw/core-domain";
import { sitzungLesen } from "../api/klient.js";
import { useOnline } from "../api/online.js";
import { behaelterAnsicht, type Unterort } from "../lokal/behaelter.js";
import { bestandLaden, useBestand, type Bestand } from "../lokal/bestand.js";
import { packstueckStandort } from "../lokal/packstueck.js";
import {
  allesDaFuer,
  fehltText,
  stammplatzSetzen,
  vollstaendigkeitVon,
} from "../lokal/stammplatz.js";
import {
  standardziel,
  statusSetzen,
  verschieben,
  zielGueltig,
  zielName,
  type Gebucht,
} from "../lokal/verschieben.js";
import { zieleLesen, zielMerken } from "../lokal/ziele.js";
import { rueckmeldungGeben } from "../scan/Scanner.jsx";
import { AllesDa } from "../ui/AllesDa.jsx";
import { AuswahlBuchen } from "../ui/AuswahlBuchen.jsx";
import { FehltListe, FehltSchild, GehoertNach, GruppenZeile, kurz } from "../ui/Inhalt.jsx";
import { Knopf, Ladeanzeige, Leer, StatusPunkt } from "../ui/bausteine.jsx";
import { StammplatzZeile } from "../ui/Stammplatz.jsx";
import { StuecklisteDrucken } from "../ui/Stueckliste.jsx";
import { useBuchungsmeldung } from "../ui/Buchungsmeldung.jsx";
import { StatusWahl } from "../ui/StatusWahl.jsx";
import { Wohin } from "../ui/Wohin.jsx";

type Betrieb = "aus" | "einbuchen" | "ausbuchen";

/** Ein neuer Behälter beginnt mit einer leeren Seite -- keine Auswahl läuft hinüber. */
export function LagerortSeite() {
  const { id = "" } = useParams();
  return <Behaelter key={id} id={id} />;
}

/**
 * Das Ziel fürs Ausbuchen: das zuletzt gewählte dieses Benutzers, sonst
 * „Ohne festen Platz“ (ADR 0013 §3). Nie der Behälter selbst.
 */
function ausbuchenZiel(
  bestand: Bestand,
  gemerkt: Ziel | null,
  hier: string,
): { ziel: Ziel; zuletzt: boolean } | null {
  if (
    gemerkt !== null &&
    zielGueltig(bestand, gemerkt) &&
    !(gemerkt.art === "lagerort" && gemerkt.id === hier)
  ) {
    return { ziel: gemerkt, zuletzt: true };
  }
  const standard = standardziel(bestand);
  if (standard === null || standard.id === hier) return null;
  return { ziel: { art: "lagerort", id: standard.id }, zuletzt: false };
}

const EBENE_TEXT = { ort: "Ort", unterebene: "Fach", packstueck: "Packstück" } as const;

function Behaelter({ id }: { id: string }) {
  const benutzer_id = sitzungLesen()?.benutzer.id ?? "";
  const online = useOnline();
  const [betrieb, setBetrieb] = useState<Betrieb>("aus");
  const [wohin, setWohin] = useState<"aus" | "verschieben" | "stammplatz">("aus");
  const [zeigeStatus, setZeigeStatus] = useState(false);
  const [zeigeListe, setZeigeListe] = useState(false);
  const [zeigeAllesDa, setZeigeAllesDa] = useState(false);
  const [gemerkt, setGemerkt] = useState<Ziel | null>(() => zieleLesen(benutzer_id).ausbuchen);
  const { setMeldung, anzeige } = useBuchungsmeldung();

  // Aus der lokalen Kopie: Was eben gescannt wurde, steht sofort in der
  // Liste, auch ohne Netz.
  const bestand = useBestand();
  const ansicht = useMemo(
    () => (bestand === undefined ? undefined : behaelterAnsicht(bestand, id)),
    [bestand, id],
  );
  const ziel = bestand === undefined ? null : ausbuchenZiel(bestand, gemerkt, id);
  const voll = useMemo(
    () => (bestand === undefined ? undefined : vollstaendigkeitVon(bestand, id)),
    [bestand, id],
  );
  const plan = useMemo(
    () => (bestand === undefined ? undefined : allesDaFuer(bestand, id)),
    [bestand, id],
  );

  /** Verschieben oder Status des Packstücks selbst. */
  const ausfuehren = async (
    tun: (b: Bestand) => Promise<Gebucht>,
    text: (b: Bestand) => string,
  ): Promise<void> => {
    try {
      const frisch = await bestandLaden();
      const gebucht = await tun(frisch);
      rueckmeldungGeben(true);
      setMeldung({ art: "gut", kopf: text(frisch), gebucht });
    } catch (fehler) {
      rueckmeldungGeben(false);
      setMeldung({
        art: "warnung",
        kopf: `Nicht gebucht: ${fehler instanceof Error ? fehler.message : String(fehler)}`,
      });
    }
    setZeigeStatus(false);
  };

  /** Den Stammplatz des Packstücks selbst ändern oder entfernen -- nur mit Netz. */
  const stammplatzAendern = async (artikel_id: string, neu: string | null): Promise<void> => {
    try {
      await stammplatzSetzen([artikel_id], neu);
      rueckmeldungGeben(true);
      const b = await bestandLaden();
      setMeldung({
        art: "gut",
        kopf:
          neu === null ? "Stammplatz entfernt" : `Stammplatz jetzt ${kurz(b.lagerorte.get(neu))}`,
      });
    } catch (fehler) {
      rueckmeldungGeben(false);
      setMeldung({
        art: "warnung",
        kopf: "Stammplatz nicht geändert",
        zusatz: fehler instanceof Error ? fehler.message : String(fehler),
      });
    }
  };

  if (bestand === undefined || ansicht === undefined || voll === undefined || plan === undefined) {
    return <Ladeanzeige was="Der Lagerort" />;
  }
  if (ansicht === null) return <Leer>Diesen Lagerort gibt es im Bestand dieses Geräts nicht.</Leer>;

  const { lagerort, oben, eigenerArtikel, plaetze, packstuecke, gruppen, anzahlArtikel } = ansicht;
  const name =
    lagerort.standardziel || lagerort.unbekannt
      ? lagerort.bezeichnung
      : `${lagerort.kennung} — ${lagerort.bezeichnung}`;

  // Ein- und Ausbuchen: ankreuzen oder scannen, dann „Fertig“ (ADR 0020).
  if (betrieb !== "aus") {
    return (
      <AuswahlBuchen
        lagerort_id={id}
        art={betrieb}
        ziel={ziel}
        aufZiel={(z) => {
          setGemerkt(z);
          zielMerken(benutzer_id, z, true);
        }}
        online={online}
        aufAbbruch={() => {
          setBetrieb("aus");
        }}
        aufGebucht={({ art, kopf, zusatz, gebucht }) => {
          setBetrieb("aus");
          setMeldung({ art, kopf, zusatz, ...(gebucht.length > 0 ? { gebucht } : {}) });
        }}
      />
    );
  }

  if (zeigeAllesDa) {
    return (
      <AllesDa
        lagerort_id={id}
        aufAbbruch={() => {
          setZeigeAllesDa(false);
        }}
        aufGebucht={(gebucht, ausgelassen) => {
          setZeigeAllesDa(false);
          rueckmeldungGeben(true);
          setMeldung({
            art: ausgelassen.length > 0 ? "warnung" : "gut",
            kopf: `✓ ${String(gebucht.length)} ${gebucht.length === 1 ? "Bewegung" : "Bewegungen"} gebucht`,
            zusatz:
              ausgelassen.length > 0
                ? `${String(ausgelassen.length)} ausgelassen: Ein Packstück hätte in sich selbst gesteckt.`
                : "Alles an seinem Stammplatz. Der Status bleibt, wie er war.",
            gebucht,
          });
        }}
      />
    );
  }
  if (wohin === "stammplatz" && eigenerArtikel !== null) {
    return (
      <Wohin
        titel={`Stammplatz — ${lagerort.kennung}`}
        artikel_id={eigenerArtikel.id}
        zweck="stammplatz"
        aufAbbruch={() => {
          setWohin("aus");
        }}
        aufWahl={(z) => {
          setWohin("aus");
          void stammplatzAendern(eigenerArtikel.id, z.id);
        }}
      />
    );
  }

  if (wohin === "verschieben" && eigenerArtikel !== null) {
    return (
      <Wohin
        titel={`Wohin? — ${lagerort.kennung} verschieben`}
        artikel_id={eigenerArtikel.id}
        aufAbbruch={() => {
          setWohin("aus");
        }}
        aufWahl={(z) => {
          setWohin("aus");
          void ausfuehren(
            (b) => verschieben(b, eigenerArtikel.id, z),
            (b) => `✓ ${lagerort.kennung} nach ${zielName(b, z)} verschoben`,
          );
        }}
      />
    );
  }

  const standort = eigenerArtikel === null ? null : packstueckStandort(bestand, lagerort.id);
  const lage =
    standort !== null
      ? standort.startsWith("in ")
        ? `steht ${standort}`
        : standort
      : oben !== null
        ? `in ${oben.kennung} — ${oben.bezeichnung}`
        : null;
  const eigenerZustand =
    eigenerArtikel === null ? undefined : bestand.zustaende.get(eigenerArtikel.id);

  const umschalten = (neu: Betrieb): void => {
    setBetrieb(neu);
    setMeldung(null);
    setZeigeStatus(false);
    setZeigeListe(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="rounded-xl bg-white p-4 dark:bg-neutral-800">
        <p className="text-sm text-neutral-500">
          {EBENE_TEXT[lagerort.ebene]}
          {lage !== null && ` · ${lage}`}
          {lagerort.mobil && " · mobil"}
        </p>
        <h1 className="text-2xl font-bold">{name}</h1>
        <p className="text-neutral-600 dark:text-neutral-300">
          {anzahlArtikel === 0 && packstuecke.length === 0
            ? "nichts gebucht"
            : `${String(anzahlArtikel)} Artikel`}
          {packstuecke.length > 0 &&
            ` · ${String(packstuecke.length)} ${packstuecke.length === 1 ? "Packstück" : "Packstücke"}`}
        </p>
        {eigenerZustand !== undefined && (
          <p className="mt-1">
            <StatusPunkt status={eigenerZustand.status} />
          </p>
        )}
        {eigenerArtikel !== null && (
          <StammplatzZeile
            bestand={bestand}
            artikel_id={eigenerArtikel.id}
            online={online}
            aufAendern={() => {
              setBetrieb("aus");
              setWohin("stammplatz");
            }}
            aufEntfernen={() => void stammplatzAendern(eigenerArtikel.id, null)}
          />
        )}
        {lagerort.standardziel && (
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
            Standardziel beim Ausbuchen. Was hier liegt, ist im Lager und verfügbar.
          </p>
        )}
        {lagerort.unbekannt && (
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
            Hier stehen verlorene und gestohlene Teile. Wiedergefunden: ausbuchen oder einfach
            scannen und woanders einbuchen – der Status wird dann wieder der Standard.
          </p>
        )}
        <VollstaendigkeitZeile voll={voll} />
      </header>

      {anzeige}

      {/*
        „Alles da“ ist der Hauptknopf, sobald etwas zurückzubuchen ist
        (ADR 0013 §8). Gebucht wird erst nach der Übersicht.
      */}
      {plan.zeilen.length > 0 && (
        <Knopf
          art="haupt"
          onClick={() => {
            setBetrieb("aus");
            setZeigeStatus(false);
            setMeldung(null);
            setZeigeAllesDa(true);
          }}
        >
          Alles da
        </Knopf>
      )}

      {/* Die Aktionen -- alle groß genug für Handschuhe. */}
      <div className="grid grid-cols-2 gap-2">
        {/* Nach „Unbekannt“ bucht nur der Status (ADR 0020). */}
        {!lagerort.unbekannt && (
          <Knopf
            onClick={() => {
              umschalten("einbuchen");
            }}
          >
            Einbuchen
          </Knopf>
        )}
        <Knopf
          onClick={() => {
            umschalten("ausbuchen");
          }}
        >
          Ausbuchen
        </Knopf>
        {eigenerArtikel !== null && (
          <>
            <Knopf
              onClick={() => {
                setBetrieb("aus");
                setWohin("verschieben");
              }}
            >
              Verschieben
            </Knopf>
            <Knopf
              onClick={() => {
                setBetrieb("aus");
                setZeigeListe(false);
                setZeigeStatus(!zeigeStatus);
              }}
            >
              Status
            </Knopf>
          </>
        )}
        {/* Immer die fünfte oder dritte -- deshalb über die ganze Breite. */}
        <div className="col-span-2 grid">
          <Knopf
            art={zeigeListe ? "haupt" : "neben"}
            onClick={() => {
              setBetrieb("aus");
              setZeigeStatus(false);
              setZeigeListe(!zeigeListe);
            }}
          >
            Stückliste
          </Knopf>
        </div>
      </div>

      {zeigeStatus && eigenerArtikel !== null && eigenerZustand !== undefined && (
        <section aria-label="Status" className="rounded-xl bg-white p-4 dark:bg-neutral-800">
          <StatusWahl
            aktuell={eigenerZustand.status}
            aufWahl={(schluessel, bezeichnung) => {
              void ausfuehren(
                (b) => statusSetzen(b, eigenerArtikel.id, schluessel),
                () => `✓ ${lagerort.kennung}: ${bezeichnung}`,
              );
            }}
          />
        </section>
      )}

      {zeigeListe && <StuecklisteDrucken bestand={bestand} lagerort_id={id} />}

      {plaetze.length > 0 && (
        <section aria-label="Plätze">
          <h2 className="mb-2 text-lg font-bold">Plätze</h2>
          <UnterortListe liste={plaetze} bestand={bestand} hier={id} />
        </section>
      )}

      {packstuecke.length > 0 && (
        <section aria-label="Packstücke">
          <h2 className="mb-2 text-lg font-bold">Packstücke</h2>
          <UnterortListe liste={packstuecke} bestand={bestand} hier={id} />
        </section>
      )}

      <section aria-label="Inhalt">
        <h2 className="mb-2 text-lg font-bold">Inhalt</h2>
        {gruppen.length === 0 ? (
          <Leer>Hier ist noch nichts gebucht.</Leer>
        ) : (
          <ul className="flex flex-col gap-2">
            {gruppen.map((g) => (
              <li key={g.bezeichnung}>
                <GruppenZeile gruppe={g} bestand={bestand} hier={id} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {voll.fehlend.length > 0 && (
        <section aria-label="Fehlt">
          <h2 className="mb-2 text-lg font-bold">Fehlt</h2>
          <FehltListe bestand={bestand} fehlend={voll.fehlend} />
        </section>
      )}

      <Link
        to={`/etiketten?lagerort=${lagerort.id}`}
        className="text-center text-sm text-blue-700 underline dark:text-blue-300"
      >
        Etikett für diesen Lagerort drucken
      </Link>
    </div>
  );
}

/**
 * „Soll 7 · da 6 · 1 fehlt · 1 fremd“ -- direkt gezählt, dazu was in den
 * Plätzen und Kisten darin fehlt (ADR 0015). Ohne Soll und ohne Fremdes steht
 * nichts da: Ein Behälter, für den keiner einen Stammplatz gesetzt hat, ist
 * weder vollständig noch unvollständig.
 */
function VollstaendigkeitZeile({ voll }: { voll: Vollstaendigkeit }) {
  if (voll.soll === 0 && voll.fremd.length === 0 && voll.fehltDarin === 0) return null;
  const fehlt = voll.fehlend.length;
  const teile = [
    `Soll ${String(voll.soll)}`,
    `da ${String(voll.da)}`,
    fehltText(fehlt) + (voll.verliehen > 0 ? ` (${String(voll.verliehen)} verliehen)` : ""),
  ];
  if (voll.fremd.length > 0) teile.push(`${String(voll.fremd.length)} fremd`);
  const alles = fehlt === 0 && voll.fehltDarin === 0;
  return (
    <p
      data-testid="vollstaendigkeit"
      className={`mt-3 rounded-lg px-3 py-2 font-semibold ${
        alles
          ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100"
          : "bg-amber-100 text-amber-950 dark:bg-amber-950 dark:text-amber-100"
      }`}
    >
      {teile.join(" · ")}
      {voll.fehltDarin > 0 && (
        <span className="block text-sm font-normal">
          darin {voll.fehltDarin === 1 ? "fehlt 1" : `fehlen ${String(voll.fehltDarin)}`}
        </span>
      )}
    </p>
  );
}

function UnterortListe({
  liste,
  bestand,
  hier,
}: {
  liste: readonly Unterort[];
  bestand: Bestand;
  hier: string;
}) {
  return (
    <ul className="flex flex-col gap-2">
      {liste.map(({ lagerort: k, anzahl }) => {
        // Jede Kiste trägt ihr eigenes Schild -- samt dem, was in ihr fehlt.
        const v = vollstaendigkeitVon(bestand, k.id);
        const hatSoll = v.soll > 0 || v.fehltDarin > 0;
        const fehlt = v.fehlend.length + v.fehltDarin;
        const kiste = k.artikel_id !== null;
        return (
          <li key={k.id}>
            <Link
              to={`/lagerort/${k.id}`}
              className={
                kiste
                  ? // Ein Packstück sieht aus wie eine Kiste (Rückmeldungen 3.2):
                    // dicker Rahmen, getönter Grund, Symbol, Anzahl, Pfeil.
                    "flex min-h-16 items-center justify-between gap-3 rounded-xl border-4 border-amber-700 bg-amber-50 p-3 active:bg-amber-100 dark:border-amber-500 dark:bg-amber-950 dark:active:bg-amber-900"
                  : "flex min-h-12 items-center justify-between gap-2 rounded-xl bg-white p-3 active:bg-neutral-100 dark:bg-neutral-800"
              }
            >
              {kiste && <KistenSymbol />}
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="font-semibold">
                  {k.kennung} — {k.bezeichnung}
                </span>
                {kiste && (
                  <span className="text-sm text-neutral-700 dark:text-neutral-200">
                    {anzahl === 1 ? "1 Gerät" : `${String(anzahl)} Geräte`}
                  </span>
                )}
                {k.artikel_id !== null && (
                  <span>
                    <GehoertNach bestand={bestand} artikel_id={k.artikel_id} hier={hier} />
                  </span>
                )}
              </span>
              <span className="flex shrink-0 items-center gap-2 text-sm">
                {hatSoll && <FehltSchild fehlt={fehlt} />}
                {kiste ? (
                  <span aria-hidden className="text-3xl leading-none font-bold">
                    ›
                  </span>
                ) : (
                  <span className="text-neutral-500">{anzahl}</span>
                )}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** Eine Kiste als Symbol -- einfarbig, damit sie im Dunkeln und in der Sonne lesbar bleibt. */
function KistenSymbol() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-8 shrink-0 text-amber-800 dark:text-amber-300"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 7l9-4 9 4v10l-9 4-9-4z" />
      <path d="M3 7l9 4 9-4" />
      <path d="M12 11v10" />
    </svg>
  );
}
